/**
 * Communities catalogue for Edmonton + Calgary.
 *
 * Powers three surfaces:
 *   1. /relocating Mapbox map (marker positions + tooltip content)
 *   2. /relocating mobile card list (fallback + supplementary)
 *   3. /neighbourhoods/:slug detail template (shared with /communities/:slug)
 *
 * Rent ranges + editorial copy are editable placeholders. Once real Scout
 * write-ups land, replace `pending: false` (already the default for the
 * fully-authored areas below) and fill in honestRead / picks / commutes.
 */

export const BEST_FOR_TAGS = {
  young: "Young professionals",
  river: "River valley",
  health: "Healthcare workers",
  students: "Students",
  arts: "Arts & nightlife",
  couples: "Couples",
  food: "Food & drink",
  creatives: "Creatives",
  quiet: "Quiet & established",
  families: "Families",
  schools: "Schools",
  value: "Value",
  transit: "Transit",
  newbuild: "New-build value",
  lake: "Lake life",
  commuters: "Commuters",
};

/* --------------------------------------------------------------------
 * MAP DEFAULTS — real Mapbox coords.
 * ------------------------------------------------------------------ */
export const EDMONTON_MAP_CENTER = { lng: -113.505, lat: 53.535 };
export const EDMONTON_MAP_ZOOM = 10.5;

/* --------------------------------------------------------------------
 * EDMONTON — 11 neighbourhoods.
 * Each row has real geo coordinates (`lng` + `lat`) used by the Mapbox
 * markers. The old stylised map fields (map: {x,y,r,side}) are gone.
 * ------------------------------------------------------------------ */
export const EDMONTON_COMMUNITIES = [
  {
    slug: "mill-woods",
    name: "Mill Woods",
    city: "edmonton",
    lng: -113.428,
    lat: 53.457,
    vibe:
      "Edmonton's great multicultural neighbourhood — value, food, and the new Valley Line LRT",
    rent: "$1,200–1,800",
    bestFor: [BEST_FOR_TAGS.families, BEST_FOR_TAGS.value, BEST_FOR_TAGS.transit],
    hero:
      "https://dcvqrrbrkrkzgjwxjgph.supabase.co/storage/v1/object/public/properties/communities/mill-woods-hero.jpg",
    walkVideoUrl: null,
    honestRead: {
      thrives:
        "Families and first-time renters who want honest value, real cultural depth, and a train they can walk to.",
      wont:
        "People whose life is downtown or Old Strathcona. Mill Woods is a world of its own — that's the point.",
      tradeoff:
        "You trade the walking-village feel of the core for genuine value, some of the best food in the city, and a real neighbourhood identity.",
    },
    picks: {
      coffee: "Little Brick South (Mill Woods Town Centre area)",
      green: "Mill Woods Park + Jackie Parker Recreation Area",
      groceries: "T&T, HMart, Save-On — all within Mill Woods",
      saturday: "Filipino, Vietnamese, Indian food strip along 34 Ave",
    },
    commutes: {
      downtown: "18 min LRT · 20 min drive",
      uofa: "20 min drive",
      airport: "20 min drive",
    },
  },
  {
    slug: "old-strathcona",
    name: "Old Strathcona",
    city: "edmonton",
    lng: -113.505,
    lat: 53.518,
    vibe: "Whyte Ave energy, campus at your door",
    rent: "$1,200–1,900",
    bestFor: [BEST_FOR_TAGS.students, BEST_FOR_TAGS.young, BEST_FOR_TAGS.arts],
    hero:
      "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
    walkVideoUrl: null,
    honestRead: {
      thrives:
        "Students and 20-somethings who want to walk to a concert, a farmers' market, and their bar all in one Saturday.",
      wont:
        "Light sleepers on a Whyte Ave weekend. Anyone who dislikes finding street parking.",
      tradeoff:
        "You trade quiet nights for the most genuinely alive stretch of street in the province.",
    },
    picks: {
      coffee: "Roast House Coffee, 87 Ave",
      green: "Mill Creek Ravine trailhead at 80 Ave",
      groceries: "Sobeys 104 St, Italian Centre on Whyte",
      saturday: "Old Strathcona Farmers' Market (year-round)",
    },
    commutes: {
      downtown: "10 min drive · 15 min transit",
      uofa: "5 min walk",
      airport: "35 min drive",
    },
  },
  {
    slug: "forest-heights",
    name: "Forest Heights",
    city: "edmonton",
    lng: -113.435,
    lat: 53.548,
    vibe:
      "River valley views minutes from downtown, without downtown prices",
    rent: "$1,100–1,700",
    bestFor: [BEST_FOR_TAGS.value, BEST_FOR_TAGS.river, BEST_FOR_TAGS.quiet],
    hero:
      "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
    walkVideoUrl: null,
    honestRead: {
      thrives:
        "Renters who want a river valley view, a short bridge to downtown, and don't mind an older building.",
      wont:
        "Anyone chasing new-build finishes or a walkable coffee strip. Forest Heights is quiet, old-stock, and proudly so.",
      tradeoff:
        "You trade brand-new amenities for real river valley vistas and honest downtown-adjacent value.",
    },
    picks: {
      coffee: "Little Brick Café (across the Dawson Bridge)",
      green: "Forest Heights Park + Dawson Park trails",
      groceries: "No Frills 118 Ave, Save-On Terrace Heights",
      saturday: "Dawson Bridge walk into downtown for brunch",
    },
    commutes: {
      downtown: "8 min drive",
      uofa: "12 min drive",
      airport: "35 min drive",
    },
  },
  {
    slug: "bonnie-doon",
    name: "Bonnie Doon",
    city: "edmonton",
    lng: -113.465,
    lat: 53.517,
    vibe: "Old trees, new LRT, river valley access",
    rent: "$1,200–1,900",
    bestFor: [BEST_FOR_TAGS.families, BEST_FOR_TAGS.value, BEST_FOR_TAGS.transit],
    hero:
      "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
    walkVideoUrl: null,
    honestRead: {
      thrives:
        "First-time renters and young families wanting a real yard, real trees, and a train to downtown that runs on time.",
      wont:
        "Anyone chasing new-build finishes or a walkable coffee strip on their own block.",
      tradeoff:
        "You trade the walking-village feel of Ritchie for genuine value — bigger homes for less, and Valley Line LRT at your door.",
    },
    picks: {
      coffee: "Iconoclast Koffiehuis, 82 Ave",
      green: "Bonnie Doon Park + Mill Creek Ravine",
      groceries: "Bonnie Doon Shopping Centre",
      saturday: "Ravine walk to Old Strathcona market",
    },
    commutes: {
      downtown: "15 min LRT · 12 min drive",
      uofa: "10 min drive",
      airport: "35 min drive",
    },
  },
  {
    slug: "windermere",
    name: "Windermere",
    city: "edmonton",
    lng: -113.620,
    lat: 53.435,
    vibe: "New-build family living with big-box convenience",
    rent: "$1,600–2,400",
    bestFor: [BEST_FOR_TAGS.families, BEST_FOR_TAGS.schools, BEST_FOR_TAGS.newbuild],
    hero:
      "https://images.unsplash.com/photo-1613977257363-707ba9348227?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
    walkVideoUrl: null,
    honestRead: {
      thrives:
        "Growing families who want a new build, a double garage, and every big-box store within a five-minute drive.",
      wont:
        "Anyone romantic about older homes or wanting to walk to a coffee shop before 8am.",
      tradeoff:
        "You trade the character of the core for square footage, ensuite laundry, and the newest schools in the city.",
    },
    picks: {
      coffee: "Starbucks Windermere",
      green: "Windermere Trails + South Terwillegar",
      groceries: "Costco, Save-On, Sobeys — all within 3 min",
      saturday: "Terwillegar Rec Centre pool + Currents shopping",
    },
    commutes: {
      downtown: "22 min drive",
      uofa: "18 min drive",
      airport: "25 min drive",
    },
  },
  {
    slug: "chappelle",
    name: "Chappelle",
    city: "edmonton",
    lng: -113.580,
    lat: 53.398,
    vibe:
      "One of Edmonton's newest family neighbourhoods — fresh builds, parks, and room to grow",
    rent: "$1,500–2,200",
    bestFor: [BEST_FOR_TAGS.families, BEST_FOR_TAGS.newbuild, BEST_FOR_TAGS.schools],
    hero:
      "https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
    walkVideoUrl: null,
    honestRead: {
      thrives:
        "Young families building their life fresh — brand new schools, parks, and neighbours all doing the same thing at the same time.",
      wont:
        "Anyone whose commute is downtown daily, or who wants character homes. Chappelle is post-2015 new-build suburbia.",
      tradeoff:
        "You trade a central location for a lot line you can afford, a garage, and a park at every second corner.",
    },
    picks: {
      coffee: "Second Cup Chappelle Square",
      green: "Chappelle West Pond + connecting trails",
      groceries: "Save-On Chappelle Square, Costco Windermere (10 min)",
      saturday: "Family bike loop to the pond and back",
    },
    commutes: {
      downtown: "28 min drive",
      uofa: "22 min drive",
      airport: "22 min drive",
    },
  },
  {
    slug: "glenora",
    name: "Glenora",
    city: "edmonton",
    lng: -113.548,
    lat: 53.545,
    vibe: "Stately, leafy, ravine-adjacent",
    rent: "$1,500–2,400",
    bestFor: [BEST_FOR_TAGS.families, BEST_FOR_TAGS.quiet, BEST_FOR_TAGS.schools],
    hero:
      "https://images.unsplash.com/photo-1567767292278-a4f21aa2d36e?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
    walkVideoUrl: null,
    honestRead: {
      thrives:
        "Families with school-age kids. Anyone who wants the river valley as their back yard and a leafy street to walk it on.",
      wont:
        "People who want nightlife within walking distance. Glenora is quiet by 9pm and proud of it.",
      tradeoff:
        "You trade Whyte Ave energy for tree-lined streets and MacKinnon Ravine at the end of the block.",
    },
    picks: {
      coffee: "Colombian Coffee Roasters, 142 St.",
      green: "MacKinnon Ravine + Riverside Trail",
      groceries: "Save-On 142 St, Andy's IGA",
      saturday: "Riverside walk + brunch at Glass Monkey",
    },
    commutes: {
      downtown: "12 min drive",
      uofa: "15 min drive",
      airport: "40 min drive",
    },
  },
  {
    slug: "grovenor",
    name: "Grovenor",
    city: "edmonton",
    // Real coords from the 10611 144 St NW listing — Grovenor sits
    // just north of Glenora, west of Westmount, and above MacKinnon
    // Ravine. Small, walkable, quietly one of Edmonton's better-value
    // inner-west pockets.
    lng: -113.569,
    lat: 53.549,
    vibe: "Quiet inner-west pocket · leafy streets · walk to Jasper Ave",
    rent: "$1,225–2,150",
    bestFor: [BEST_FOR_TAGS.families, BEST_FOR_TAGS.quiet, BEST_FOR_TAGS.value],
    hero:
      "https://dcvqrrbrkrkzgjwxjgph.supabase.co/storage/v1/object/public/properties/communities/grovenor-hero.jpg",
    walkVideoUrl: null,
    honestRead: {
      thrives:
        "Families and first-time buyers who want a quiet, mature street a short bike from Glenora, Westmount, and the river valley — without paying Glenora rent.",
      wont:
        "People who need a busy main street at their door. Grovenor is residential first, retail later — Stony Plain Rd and 149 St handle the errands.",
      tradeoff:
        "You trade a walking-village high street for one of the calmest, most established pockets in the inner west — and a 12-minute drive to downtown.",
    },
    picks: {
      coffee: "Cafe Bicyclette (French Quarter, short bike east)",
      green: "MacKinnon Ravine + the 142 St bike route to the river",
      groceries: "Save-On Foods on 142 St, Sunterra Market at Oliver Square",
      saturday: "Ravine walk → Glenora brunch → 124 St gallery walk",
    },
    commutes: {
      downtown: "12 min drive · 20 min bus",
      uofa: "17 min drive",
      airport: "40 min drive",
    },
  },
  {
    slug: "westmount",
    name: "Westmount",
    city: "edmonton",
    lng: -113.532,
    lat: 53.552,
    vibe: "Gallery walks on 124 Street and character homes",
    rent: "$1,300–2,000",
    bestFor: [BEST_FOR_TAGS.creatives, BEST_FOR_TAGS.couples, BEST_FOR_TAGS.quiet],
    hero:
      "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
    walkVideoUrl: null,
    honestRead: {
      thrives:
        "Artists, gallery hoppers, and anyone who considers a good neighbourhood restaurant a birthright.",
      wont:
        "New-build seekers. This is character homes, low-rises, and the occasional 1920s duplex — beautiful, but old bones.",
      tradeoff:
        "You trade central AC and heated floors for a walk to five galleries, three great restaurants, and the second Saturday art walk.",
    },
    picks: {
      coffee: "Duchess Bake Shop (also lunch)",
      green: "Coronation Park",
      groceries: "Sobeys 124, Meat Street Pies",
      saturday: "The Art Walk, 124 St. (summer Saturdays)",
    },
    commutes: {
      downtown: "8 min drive · 15 min bus",
      uofa: "15 min drive",
      airport: "38 min drive",
    },
  },
  {
    slug: "crestwood",
    name: "Crestwood",
    city: "edmonton",
    lng: -113.560,
    lat: 53.538,
    vibe: "Ravine-edge prestige in the city's quiet west",
    rent: "$1,600–2,500",
    bestFor: [BEST_FOR_TAGS.families, BEST_FOR_TAGS.quiet, BEST_FOR_TAGS.river],
    hero:
      "https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
    walkVideoUrl: null,
    honestRead: {
      thrives:
        "Families and downshifters who want a mature street, real trees, and a ravine trail at the end of the block.",
      wont:
        "Anyone who wants a walkable coffee strip. Crestwood is residential — coffee is a short drive away.",
      tradeoff:
        "You trade shops-on-your-block for one of the quietest, greenest pockets in the city — with the river valley steps away.",
    },
    picks: {
      coffee: "Colombian Coffee Roasters (Glenora, 5 min drive)",
      green: "MacKinnon Ravine + Buena Vista Park",
      groceries: "Save-On 149 St, Andy's IGA",
      saturday: "Ravine walk, then brunch on 124 St",
    },
    commutes: {
      downtown: "14 min drive",
      uofa: "18 min drive",
      airport: "40 min drive",
    },
  },
  {
    slug: "ottewell",
    name: "Ottewell",
    city: "edmonton",
    lng: -113.440,
    lat: 53.530,
    vibe: "Mature trees, big lots, schools you can walk to",
    rent: "$1,300–1,900",
    bestFor: [BEST_FOR_TAGS.families, BEST_FOR_TAGS.schools, BEST_FOR_TAGS.quiet],
    hero:
      "https://images.unsplash.com/photo-1580587771525-78b9dba3b914?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
    walkVideoUrl: null,
    honestRead: {
      thrives:
        "Families who want big yards, walkable schools, and a quiet mature street — without paying Glenora prices.",
      wont:
        "Anyone under 30 looking for scene. Ottewell is family-first and calm.",
      tradeoff:
        "You trade proximity to Whyte for a bigger lot, a walkable K-6, and a quieter Sunday morning.",
    },
    picks: {
      coffee: "Iconoclast Koffiehuis (Bonnie Doon, 5 min)",
      green: "Ottewell Park + Gold Bar Ravine access",
      groceries: "Save-On 82 Ave, No Frills 76 Ave",
      saturday: "Gold Bar Park picnic + river valley walk",
    },
    commutes: {
      downtown: "14 min drive",
      uofa: "12 min drive",
      airport: "32 min drive",
    },
  },
  {
    slug: "mayfield",
    name: "Mayfield",
    city: "edmonton",
    lng: -113.620,
    lat: 53.545,
    vibe: "West-end value with quick highway access everywhere",
    rent: "$1,100–1,600",
    bestFor: [BEST_FOR_TAGS.value, BEST_FOR_TAGS.commuters, BEST_FOR_TAGS.quiet],
    hero:
      "https://images.unsplash.com/photo-1502005229762-cf1b2da7c5d6?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
    walkVideoUrl: null,
    honestRead: {
      thrives:
        "Commuters who work west of the city, and renters who want honest value close to Whitemud + Yellowhead access.",
      wont:
        "Anyone wanting a walkable amenity strip. Mayfield is residential — you drive to your shops.",
      tradeoff:
        "You trade downtown-adjacency for cheaper rent and a highway on-ramp that gets you anywhere in ten minutes.",
    },
    picks: {
      coffee: "Iconic Coffee House, Mayfield Common",
      green: "Coronation Park (12 min bike)",
      groceries: "Save-On Mayfield Common, T&T west",
      saturday: "West Edmonton Mall detour or a river-valley drive west",
    },
    commutes: {
      downtown: "15 min drive (Whitemud)",
      uofa: "18 min drive",
      airport: "35 min drive",
    },
  },
];

export const getCommunity = (slug) =>
  [...EDMONTON_COMMUNITIES, ...CALGARY_COMMUNITIES].find(
    (c) => c.slug === slug,
  ) || null;

/* --------------------------------------------------------------------
 * CALGARY — companion catalogue (unchanged).
 * ------------------------------------------------------------------ */
export const CALGARY_COMMUNITIES = [
  {
    slug: "mission",
    name: "Mission",
    city: "calgary",
    pending: true,
    vibe: "Boutique high-street living on the Elbow River",
    rent: "$1,700–2,700",
    bestFor: [BEST_FOR_TAGS.young, BEST_FOR_TAGS.couples, BEST_FOR_TAGS.food],
    hero:
      "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
  },
  {
    slug: "bridgeland",
    name: "Bridgeland",
    city: "calgary",
    pending: true,
    vibe: "Bakeries, skyline, and a two-minute walk to the Zoo bridge",
    rent: "$1,500–2,400",
    bestFor: [BEST_FOR_TAGS.young, BEST_FOR_TAGS.food, BEST_FOR_TAGS.transit],
    hero:
      "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
  },
  {
    slug: "kensington",
    name: "Kensington",
    city: "calgary",
    pending: true,
    vibe: "Bookshops and a train downtown",
    rent: "$1,400–2,200",
    bestFor: [BEST_FOR_TAGS.young, BEST_FOR_TAGS.arts, BEST_FOR_TAGS.transit],
    hero:
      "https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
  },
  {
    slug: "marda-loop",
    name: "Marda Loop",
    city: "calgary",
    pending: true,
    vibe: "The village-in-the-city energy of 33rd Ave",
    rent: "$1,500–2,400",
    bestFor: [BEST_FOR_TAGS.couples, BEST_FOR_TAGS.families, BEST_FOR_TAGS.food],
    hero:
      "https://images.unsplash.com/photo-1567767292278-a4f21aa2d36e?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
  },
  {
    slug: "inglewood",
    name: "Inglewood",
    city: "calgary",
    pending: true,
    vibe: "Vinyl shops, the Bird Sanctuary, and Calgary's oldest main street",
    rent: "$1,600–2,600",
    bestFor: [BEST_FOR_TAGS.creatives, BEST_FOR_TAGS.couples, BEST_FOR_TAGS.food],
    hero:
      "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
  },
  {
    slug: "hillhurst",
    name: "Hillhurst",
    city: "calgary",
    pending: true,
    vibe: "Riley Park, Kensington Rd. shops, and river-north quiet",
    rent: "$1,600–2,500",
    bestFor: [BEST_FOR_TAGS.families, BEST_FOR_TAGS.quiet, BEST_FOR_TAGS.river],
    hero:
      "https://images.unsplash.com/photo-1580587771525-78b9dba3b914?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
  },
];
